/**
 * RichTextControl — the `richtext` control type (Site Builder properties panel).
 *
 * Used by the imported-site "Host-Styled Text" block (content_host_text), whose body the backend
 * allowlist-sanitises (site_components/html_sanitizer.py: p, br, h3, h4, strong/b, em/i, u, s, a,
 * ul/ol/li, blockquote, code, sub, sup; a[href] with http/https/mailto/tel/relative only). The
 * editor schema is deliberately limited to a subset of that list, so what the user sees here is
 * what the page serves: no images, tables, embeds, colours, alignment or raw-HTML mode. Pasted
 * Word / web content is reduced to this schema by ProseMirror's parser (unknown tags and every
 * style/class attribute are dropped; h1/h2 become paragraphs).
 * specs/completed/imported-site-deep-insert-zones.md Phase 3.
 *
 * NOT EDMRichEditor: that one is email-coupled (image upload, inline TextAlign styles, merge tags).
 *
 * Persistence: emits editor HTML ("" when empty) on every change; the parent debounces the PUT
 * (ImportedSiteEditor, 600 ms per section). A `value` change from props is applied only while the
 * editor is NOT focused, so a server round-trip can never move the cursor mid-typing.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading3, Heading4, List, ListOrdered, Quote,
  Link2, Unlink, RemoveFormatting, Undo2, Redo2,
} from 'lucide-react';
import { GlassButton } from '../ui';
import { checkLinkHref, isSafeLinkHref } from './richTextLinks';
import './RichTextControl.css';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  label?: string;
}

// Link mark with ONLY href. The stock mark also parses target/rel/class from pasted HTML; the
// served page gets a plain <a href> (internal links on a legal page should not force a new tab, and
// the sanitiser would rewrite rel anyway), so the editor must not hold attributes it won't serve.
const HrefOnlyLink = Link.extend({
  addAttributes() {
    return { href: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute('href') } };
  },
});

// StarterKit's TrailingNode keeps an empty paragraph after a final heading/list/quote so the caret
// can get below it. That is editing scaffolding, not content: drop trailing empty paragraphs from
// what is stored, so the page never ships an extra empty <p>.
const toStored = (html: string) => html.replace(/(?:<p><\/p>)+$/, '');

const RichTextControl: React.FC<Props> = ({ value, onChange, placeholder, label = 'Body' }) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const openLinkRef = useRef<() => void>(() => {});
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');
  const [linkError, setLinkError] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: false,           // replaced by HrefOnlyLink below
      } as any),
      HrefOnlyLink.configure({
        openOnClick: false,
        // autolink makes the mark inclusive, so typing straight after a link would keep extending
        // it. Links are added explicitly (toolbar / Ctrl+K) or by pasting a URL over a selection.
        autolink: false,
        linkOnPaste: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: { target: null, rel: null, class: null },   // no default target/rel
        // Also guards pasted / autolinked hrefs: a javascript: link is parsed as plain text.
        isAllowedUri: (url: string) => isSafeLinkHref(url),
      }),
      Placeholder.configure({ placeholder: placeholder || 'Write the text…' }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'aime-rt-content',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          openLinkRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.isEmpty ? '' : toStored(ed.getHTML()));
    },
  });

  // Toolbar active/disabled states (TipTap v3 no longer re-renders on every transaction).
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed ? {
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      h3: ed.isActive('heading', { level: 3 }),
      h4: ed.isActive('heading', { level: 4 }),
      bullet: ed.isActive('bulletList'),
      ordered: ed.isActive('orderedList'),
      quote: ed.isActive('blockquote'),
      link: ed.isActive('link'),
      canUndo: ed.can().undo(),
      canRedo: ed.can().redo(),
    } : null,
  });

  // Sync from props only while not focused (a debounced PUT / list refresh must not jump the cursor).
  useEffect(() => {
    if (!editor || editor.isDestroyed || editor.isFocused) return;
    const current = editor.isEmpty ? '' : toStored(editor.getHTML());
    if ((value || '') !== current) editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  const openLink = () => {
    if (!editor) return;
    setLinkDraft(editor.getAttributes('link').href || '');
    setLinkError('');
    setLinkOpen(true);
  };
  openLinkRef.current = openLink;

  useEffect(() => { if (linkOpen) linkInputRef.current?.focus(); }, [linkOpen]);

  const closeLink = () => {
    setLinkOpen(false);
    setLinkError('');
    editor?.commands.focus();
  };

  const applyLink = () => {
    if (!editor) return;
    const res = checkLinkHref(linkDraft);
    if (!res.ok) { setLinkError(res.error); return; }
    const { from, to } = editor.state.selection;
    if (from === to && !editor.isActive('link')) {
      // Nothing selected: insert the address itself as the link text.
      editor.chain().focus().insertContent({
        type: 'text', text: res.href, marks: [{ type: 'link', attrs: { href: res.href } }],
      }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: res.href }).run();
    }
    setLinkOpen(false);
    setLinkError('');
  };

  const btn = (title: string, icon: React.ReactNode, run: () => void, active = false, disabled = false) => (
    <GlassButton
      key={title}
      variant="ghost"
      size="sm"
      title={title}
      aria-label={title}
      aria-pressed={active}
      icon={icon}
      disabled={!editor || disabled}
      className={`aime-rt-btn${active ? ' is-active' : ''}`}
      onClick={run}
    />
  );

  const chain = () => editor!.chain().focus();

  return (
    <div className="aime-rt">
      {label && <div className="aime-rt-label">{label}</div>}
      <div className="aime-rt-frame">
        <div className="aime-rt-toolbar" role="toolbar" aria-label={`${label} formatting`}>
          {btn('Bold', <Bold size={15} />, () => chain().toggleBold().run(), !!state?.bold)}
          {btn('Italic', <Italic size={15} />, () => chain().toggleItalic().run(), !!state?.italic)}
          {btn('Underline', <UnderlineIcon size={15} />, () => chain().toggleUnderline().run(), !!state?.underline)}
          {btn('Heading 3', <Heading3 size={15} />, () => chain().toggleHeading({ level: 3 }).run(), !!state?.h3)}
          {btn('Heading 4', <Heading4 size={15} />, () => chain().toggleHeading({ level: 4 }).run(), !!state?.h4)}
          {btn('Bullet list', <List size={15} />, () => chain().toggleBulletList().run(), !!state?.bullet)}
          {btn('Numbered list', <ListOrdered size={15} />, () => chain().toggleOrderedList().run(), !!state?.ordered)}
          {btn('Quote', <Quote size={15} />, () => chain().toggleBlockquote().run(), !!state?.quote)}
          {btn('Add link', <Link2 size={15} />, openLink, !!state?.link)}
          {btn('Remove link', <Unlink size={15} />, () => chain().extendMarkRange('link').unsetLink().run(), false, !state?.link)}
          {btn('Clear formatting', <RemoveFormatting size={15} />, () => chain().unsetAllMarks().clearNodes().run())}
          {btn('Undo', <Undo2 size={15} />, () => chain().undo().run(), false, !state?.canUndo)}
          {btn('Redo', <Redo2 size={15} />, () => chain().redo().run(), false, !state?.canRedo)}
        </div>
        {linkOpen && (
          <div className="aime-rt-link" role="group" aria-label="Link address">
            <input
              ref={linkInputRef}
              type="text"
              value={linkDraft}
              aria-label="Link address"
              aria-invalid={!!linkError}
              placeholder="https://…, /page, mailto:…, tel:…"
              onChange={e => { setLinkDraft(e.target.value); if (linkError) setLinkError(''); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                else if (e.key === 'Escape') { e.preventDefault(); closeLink(); }
              }}
            />
            <div className="aime-rt-link-actions">
              <GlassButton variant="primary" size="sm" onClick={applyLink}>Apply</GlassButton>
              <GlassButton variant="secondary" size="sm" onClick={closeLink}>Cancel</GlassButton>
            </div>
            {linkError && <div className="aime-rt-link-error" role="alert">{linkError}</div>}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextControl;
